import { isString } from "@sinch/feature-flags";
import { RUDDERSTACK_PERSISTED_KEYS } from "../../analytics/rudderstack";
import { STABLE_ID_KEY } from "../../featureFlags";
import { INBOX_PERSISTED_KEYS } from "../../hooks/useInboxConfig.constants";
import {
  CONTACTS_V2_LOCAL_STORAGE_KEYS,
  MFE_LOGIN_LOCAL_STORAGE_KEYS,
  REPORTING_LOCAL_STORAGE_KEYS,
  CACHED_COUNTRY_KEY,
} from "./keys";

export const setLocalStorage = <T>(key: string, value: T): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Could not set data for ${key} in localStorage`, error);
  }
};

export const removeLocalStorage = (id: string) => {
  try {
    localStorage.removeItem(id);
  } catch (e) {
    console.error("Could not remove data from localStorage", e);
    // do nothing
  }
};

export const clearLocalStorage = (extraPersistKeys: string[]) => {
  const persistKeys = [
    CACHED_COUNTRY_KEY,
    STABLE_ID_KEY,
    "shell:is-sidebar-collapsed",
    ...REPORTING_LOCAL_STORAGE_KEYS,
    ...INBOX_PERSISTED_KEYS,
    ...CONTACTS_V2_LOCAL_STORAGE_KEYS,
    ...RUDDERSTACK_PERSISTED_KEYS,
    ...MFE_LOGIN_LOCAL_STORAGE_KEYS,
    ...extraPersistKeys,
  ];

  const currentKeys: string[] = [];
  for (let index = 0; index < localStorage.length; index++) {
    const storageKey = localStorage.key(index);
    if (storageKey) {
      currentKeys.push(storageKey);
    }
  }

  currentKeys
    .filter((key) => !persistKeys.includes(key))
    .forEach((key) => localStorage.removeItem(key));
};

type GetStorageData = {
  (id: string, options?: { useSessionStorageAsFallback?: boolean }):
    | string
    | undefined;
};
export const getStorageData: GetStorageData = (id, options = {}) => {
  const { useSessionStorageAsFallback = true } = options;
  try {
    let rawData = localStorage.getItem(id);

    if (!rawData && useSessionStorageAsFallback) {
      rawData = sessionStorage.getItem(id);
    }

    return rawData ? JSON.parse(rawData) : undefined;
  } catch (e) {
    return undefined;
  }
};

export const isStringOrNull = (x: unknown, flag: string): x is string | null =>
  x === null || isString(x, flag);

export const getLocalStorage = <T>(
  key: string,
  validator: (x: unknown, key: string) => x is T,
  fallback: T
) => {
  try {
    const x = JSON.parse(localStorage.getItem(key) ?? "null");
    return validator(x, key) ? x : fallback;
  } catch {
    return fallback;
  }
};

const storageListeners: {
  [key: string]: { [cbkey: string]: () => void };
} = {};

const {
  setItem: originalSetItem,
  removeItem: originalRemoveItem,
  clear: originalClear,
} = localStorage;

localStorage.setItem = function (key, value) {
  originalSetItem.call(this, key, value);
  setTimeout(() => {
    Object.values(storageListeners[key] || {})?.forEach((callback) =>
      callback()
    );
  }, 0);
};

localStorage.removeItem = function (key) {
  originalRemoveItem.call(this, key);
  setTimeout(() => {
    Object.values(storageListeners[key] || {})?.forEach((callback) =>
      callback()
    );
  }, 0);
};

localStorage.clear = function () {
  originalClear.call(this);
  setTimeout(() => {
    Object.values(storageListeners).forEach((callbacks) => {
      Object.values(callbacks).forEach((callback) => callback());
    });
  }, 0);
};

export const listenToLocalStorageChange = (
  key: string,
  callback: () => void
) => {
  const cbKey = Math.random().toString(36).substring(2, 7);
  storageListeners[key] = { ...storageListeners[key], [cbKey]: callback };

  return () => {
    const { [cbKey]: _deleted, ...rest } = storageListeners[key] || {};
    storageListeners[key] = { ...rest };
  };
};
